import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import VimeoPlayer from "@vimeo/player";

import LottieLayout from "./Layouts/Lottie";
import ImageLayout from "./Layouts/Image";
import VideoLayout from "./Layouts/Video";
import ProjectTypes from "./ProjectTypes";
import viewportArrowLeftIcon from "/assets/viewport-arrow-left.svg";
import viewportArrowRightIcon from "/assets/viewport-arrow-right.svg";

export default function ProjectCard({
  project,
}) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const playerRefs = useRef({});
  const currentLayout = project.layouts[currentIndex];

  const previousIndex = () => {
    setCurrentIndex(currentIndex => (currentIndex <= 0) ? project.layouts.length - 1 : currentIndex - 1);
  };

  const nextIndex = () => {
    setCurrentIndex(currentIndex => (currentIndex >= project.layouts.length - 1) ? 0 : currentIndex + 1);
  };

  const stopAllExcept = (layoutId) => {
    project.layouts.forEach((layout) => {
      if (layout.id !== layoutId && playerRefs.current[layout.id])
        playerRefs.current[layout.id].pause();
    });
  };

  useEffect(() => setCurrentIndex(0), [project.layouts]);
  useEffect(() => {
    if (!currentLayout || currentLayout.layout_type_codename !== "video") return;

    if (!playerRefs.current[currentLayout.id]) {
      playerRefs.current[currentLayout.id] = new VimeoPlayer(`vimeo-${currentLayout.id}`);
      playerRefs.current[currentLayout.id].ready().then(() => {
        if (currentLayout.play_mode === "loop")
          playerRefs.current[currentLayout.id].play();
      });
    } else {
      if (currentLayout.play_mode === "loop")
        playerRefs.current[currentLayout.id].play();
    }

    stopAllExcept(currentLayout.id);
  }, [currentIndex, project.layouts]);

  return currentLayout && (
    <div className="project-gallery">
      <div className="project-gallery__viewport-and-info">
        <div className="project-gallery__viewport">
          {project.layouts.length > 1 && (
            <>
              <div className="project-gallery__viewport__left" onClick={previousIndex}><img src={viewportArrowLeftIcon} /></div>
              <div className="project-gallery__viewport__right" onClick={nextIndex}><img src={viewportArrowRightIcon} /></div>
            </>
          )}

          {project.layouts.map((layout, index) => {
            return (
              <div
                key={layout.id}
                className={classNames("project-gallery__layout", { "project-gallery__layout--show": currentIndex === index })}
              >
                {layout.layout_type_codename === "image" && (
                  <ImageLayout key={layout.id} layout={layout} />
                )}

                {layout.layout_type_codename === "video" && (
                  <VideoLayout key={layout.id} layout={layout} />
                )}

                {layout.layout_type_codename === "lottie" && (
                  <LottieLayout key={layout.id} layout={layout} />
                )}
              </div>
            );
          })}

          <div className="project-gallery__indicators">
            {project.layouts.map((layout, index) => (
              <div
                className={`project-gallery__indicator ${currentIndex === index ? "project-gallery__indicator--current" : ""}`}
                key={layout.id}
              ></div>
            ))}
          </div>
        </div>

        <div className="project-gallery__info">
          <div className="project-gallery__info__top">
            <h2><a href={project.url}>{project.title}</a></h2>

            <ProjectTypes project_types={project.project_types} />
          </div>

          <div className="project-gallery__info__text">
            <p>{currentLayout.description}</p>
            <div><a href={project.url} className="btn">{t('common.more')}</a></div>
          </div>
        </div>
      </div>

      <div className="project-gallery__thumbnails">
        {project.layouts.map((layout, index) => {
          const isSelected = index === currentIndex;

          return (
            <div key={layout.id} className={classNames("project-gallery__thumbnail", { "project-gallery__thumbnail--selected": isSelected })} onClick={() => setCurrentIndex(index)}>
              {layout.layout_type_codename === "image" && (
                <img src={layout.image.url} />
              )}
              {layout.layout_type_codename === "video" && (
                <img src={layout.thumbnail_url} />
              )}
              {layout.layout_type_codename === "lottie" && (
                <div className="project-gallery__thumbnail__lottie"></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
